// src/context/AuthContext.js
import { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { Base_URL, tempURl } from "../url";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(false); // For button loading states
  const navigate = useNavigate();

  // ✅ SAFE AUTH CHECK
  useEffect(() => {
    const checkAuth = () => {
      try {
        const storedUser = localStorage.getItem("userDetails");
        console.log(storedUser, "storedUser");

        // 🚨 INVALID STORAGE GUARDS
        if (
          !storedUser ||
          storedUser === "undefined" ||
          storedUser === "null"
        ) {
          localStorage.removeItem("userDetails");
          localStorage.removeItem("token");
          localStorage.removeItem("isMicrosoftUser");
          localStorage.removeItem("isGoogleUser");
          setUser(null);
          setIsAuthenticated(false);
          return;
        }

        const parsedUser = JSON.parse(storedUser);

        if (!parsedUser || typeof parsedUser !== "object") {
          throw new Error("Invalid user object");
        }

        setUser(parsedUser);
        setIsAuthenticated(true);
        console.log("Auth restored");
      } catch (error) {
        console.error("Auth check failed:", error);
        localStorage.removeItem("userDetails");
        localStorage.removeItem("token");
        localStorage.removeItem("isMicrosoftUser");
        localStorage.removeItem("isGoogleUser");
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // ✅ PASSWORD LOGIN
  const login = async (username, password) => {
    setAuthLoading(true);
    try {
      const response = await axios.post(`${Base_URL}/Logins`, {
        username,
        password,
      });

      if (response.status !== 200 || !response.data?.userDetails) {
        throw new Error("Invalid login response");
      }

      toast.success("🎉 Login Successfully");

      localStorage.setItem("token", response.data.token);
      localStorage.setItem("username", username);
      localStorage.setItem(
        "userDetails",
        JSON.stringify(response.data.userDetails),
      );
      localStorage.setItem("isMicrosoftUser", "false");
      localStorage.setItem("isGoogleUser", "false");

      setUser(response.data.userDetails);
      setIsAuthenticated(true);

      navigate("/home");
      return true;
    } catch (error) {
      console.error("Login error:", error);
      toast.error(`🦄 ${error.response?.data?.message || error.message}`);
      return false;
    } finally {
      setAuthLoading(false);
    }
  };

  // ✅ MICROSOFT LOGIN
  const loginWithMicrosoft = (userDetails) => {
    if (!userDetails || typeof userDetails !== "object") {
      console.error("Invalid Microsoft userDetails");
      toast.error("Microsoft login failed: Invalid user data");
      return;
    }

    localStorage.setItem("userDetails", JSON.stringify(userDetails));
    localStorage.setItem("isMicrosoftUser", "true");
    localStorage.setItem("isGoogleUser", "false");

    setUser(userDetails);
    setIsAuthenticated(true);
    toast.success("Microsoft login successful!");
    navigate("/home");
  };

  // ✅ GOOGLE LOGIN - Exchange code for user details
  const loginWithGoogle = async (code) => {
    setAuthLoading(true);
    try {
      const response = await axios.post(`${tempURl}/auth/google/exchange`, {
        code: code,
      });

      if (response.status !== 200 || !response.data?.UserDetails) {
        throw new Error(response.data?.message || "Google login failed");
      }

      const userDetails = response.data.UserDetails;

      // Store user data
      localStorage.setItem("userDetails", JSON.stringify(userDetails));
      localStorage.setItem("isGoogleUser", "true");
      localStorage.setItem("isMicrosoftUser", "false");

      if (userDetails.token) {
        localStorage.setItem("token", userDetails.token);
      }

      setUser(userDetails);
      setIsAuthenticated(true);
      toast.success("🎉 Google Login Successful!");

      navigate("/home");
      return userDetails;
    } catch (error) {
      console.error("Google login error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.Message ||
        error.message ||
        "Google login failed";
      toast.error(`🦄 ${errorMessage}`);
      throw error;
    } finally {
      setAuthLoading(false);
    }
  };

  // ✅ GOOGLE LOGIN - Token-based (for redirect flow)
  const loginWithGoogleToken = async (token) => {
    setAuthLoading(true);
    try {
      const response = await axios.get(`${tempURl}/auth/userinfo`, {
        params: { token: token },
      });

      if (response.status !== 200 || !response.data?.userId) {
        throw new Error("Invalid user data from server");
      }

      const userDetails = response.data;

      // Store user data
      localStorage.setItem("userDetails", JSON.stringify(userDetails));
      localStorage.setItem("token", token);
      localStorage.setItem("isGoogleUser", "true");
      localStorage.setItem("isMicrosoftUser", "false");

      setUser(userDetails);
      setIsAuthenticated(true);
      toast.success("🎉 Google Login Successful!");

      navigate("/home");
      return userDetails;
    } catch (error) {
      console.error("Google token login error:", error);
      toast.error(
        `🦄 ${error.response?.data?.message || error.message || "Google login failed"}`,
      );
      throw error;
    } finally {
      setAuthLoading(false);
    }
  };

  // ✅ GENERIC TOKEN LOGIN (for any provider)
  const loginWithToken = async (token) => {
    setAuthLoading(true);
    try {
      const response = await axios.get(`${tempURl}/auth/userinfo`, {
        params: { token: token },
      });

      if (response.status !== 200 || !response.data?.userId) {
        throw new Error("Invalid token or expired session");
      }

      const userDetails = response.data;

      localStorage.setItem("userDetails", JSON.stringify(userDetails));
      localStorage.setItem("token", token);

      setUser(userDetails);
      setIsAuthenticated(true);

      return userDetails;
    } catch (error) {
      console.error("Token login error:", error);
      // Don't show toast here as it might be called on page load
      throw error;
    } finally {
      setAuthLoading(false);
    }
  };

  // ✅ LOGOUT (FULL CLEAN)
  const logout = () => {
    localStorage.removeItem("userDetails");
    localStorage.removeItem("token");
    localStorage.removeItem("username");
    localStorage.removeItem("isMicrosoftUser");
    localStorage.removeItem("isGoogleUser");

    setUser(null);
    setIsAuthenticated(false);
    toast.info("👋 Logged out successfully");
    navigate("/");
  };

  // ✅ CHECK IF USER IS FROM SPECIFIC PROVIDER
  const isMicrosoftUser = () => {
    return localStorage.getItem("isMicrosoftUser") === "true";
  };

  const isGoogleUser = () => {
    return localStorage.getItem("isGoogleUser") === "true";
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    authLoading,
    login,
    loginWithMicrosoft,
    loginWithGoogle,
    loginWithGoogleToken,
    loginWithToken,
    logout,
    isMicrosoftUser,
    isGoogleUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

// ✅ SAFE HOOK
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// // src/context/AuthContext.js
// import { createContext, useContext, useState, useEffect } from "react";
// import { useNavigate } from "react-router-dom";
// import axios from "axios";
// import { toast } from "react-toastify";
// import { Base_URL } from "../url";

// const AuthContext = createContext();

// export const AuthProvider = ({ children }) => {
//   const [user, setUser] = useState(null);
//   const [isAuthenticated, setIsAuthenticated] = useState(false);
//   const [loading, setLoading] = useState(true);
//   const navigate = useNavigate();

//   // ✅ SAFE AUTH CHECK
//   useEffect(() => {
//     const checkAuth = () => {
//       try {
//         const storedUser = localStorage.getItem("userDetails");
//         console.log(storedUser, "storedUser");

//         // 🚨 INVALID STORAGE GUARDS
//         if (
//           !storedUser ||
//           storedUser === "undefined" ||
//           storedUser === "null"
//         ) {
//           localStorage.removeItem("userDetails");
//           setUser(null);
//           setIsAuthenticated(false);
//           return;
//         }

//         const parsedUser = JSON.parse(storedUser);

//         if (!parsedUser || typeof parsedUser !== "object") {
//           throw new Error("Invalid user object");
//         }

//         setUser(parsedUser);
//         setIsAuthenticated(true);
//         console.log("Auth restored");
//       } catch (error) {
//         console.error("Auth check failed:", error);
//         localStorage.removeItem("userDetails");
//         setUser(null);
//         setIsAuthenticated(false);
//       } finally {
//         setLoading(false);
//       }
//     };

//     checkAuth();
//   }, []);

//   // ✅ MICROSOFT LOGIN
//   const loginWithMicrosoft = (userDetails) => {
//     if (!userDetails || typeof userDetails !== "object") {
//       console.error("Invalid Microsoft userDetails");
//       return;
//     }

//     localStorage.setItem("userDetails", JSON.stringify(userDetails));
//     localStorage.setItem("isMicrosoftUser", "true");

//     setUser(userDetails);
//     setIsAuthenticated(true);
//   };

//   // ✅ PASSWORD LOGIN
//   const login = async (username, password) => {
//     try {
//       const response = await axios.post(`${Base_URL}/Logins`, {
//         username,
//         password,
//       });

//       if (response.status !== 200 || !response.data?.userDetails) {
//         throw new Error("Invalid login response");
//       }

//       toast.success("🎉 Login Successfully");

//       localStorage.setItem("token", response.data.token);
//       localStorage.setItem("username", username);
//       localStorage.setItem(
//         "userDetails",
//         JSON.stringify(response.data.userDetails)
//       );
//       localStorage.setItem("isMicrosoftUser", "false");

//       setUser(response.data.userDetails);
//       setIsAuthenticated(true);

//       navigate("/home");
//       return true;
//     } catch (error) {
//       console.error("Login error:", error);
//       toast.error(`🦄 ${error.response?.data?.message || error.message}`);
//       return false;
//     }
//   };

//   // ✅ LOGOUT (FULL CLEAN)
//   const logout = () => {
//     localStorage.removeItem("userDetails");
//     localStorage.removeItem("token");
//     localStorage.removeItem("username");
//     localStorage.removeItem("isMicrosoftUser");

//     setUser(null);
//     setIsAuthenticated(false);
//     navigate("/");
//   };

//   const value = {
//     user,
//     isAuthenticated,
//     loading,
//     login,
//     loginWithMicrosoft,
//     logout,
//   };

//   return (
//     <AuthContext.Provider value={value}>
//       {!loading && children}
//     </AuthContext.Provider>
//   );
// };

// // ✅ SAFE HOOK
// export const useAuth = () => {
//   const context = useContext(AuthContext);
//   if (!context) {
//     throw new Error("useAuth must be used within an AuthProvider");
//   }
//   return context;
// };
